import { useEffect } from "react";
import { normalizeDialable } from "../data/phoneNumbers";

/** Legacy pages mark a dialable cell with this. Nothing else is clickable. */
export const CALL_ATTRIBUTE = "data-call-number";

/** Body flag the stylesheet keys its cell rules off. */
const STATE_ATTRIBUTE = "data-softphone";

export type ClickToCallState = "connecting" | "ready" | "busy" | "blocked";

/* eslint-disable no-unused-vars */
type UseClickToCall = {
  state: ClickToCallState;
  blockedReason: string;
  onCall: (phoneNumber: string) => void;
};
/* eslint-enable no-unused-vars */

/**
 * Click-to-call over server-rendered markup React does not own.
 *
 * show_customer1.php prints each customer's numbers as plain text carrying
 * data-call-number, and this attaches one delegated listener rather than asking
 * a legacy while-loop to render a control per row. Three rules follow:
 *
 * - A number normalizeDialable() refuses loses the attribute on the way in. It
 *   would otherwise reach the backend, come back "ese numero no se puede
 *   marcar", and leave a failed row behind. A cell that cannot place a call
 *   must never look like it can -- and must not carry a tooltip either, because
 *   a bad number in the customer record is not something the rep can fix here.
 * - "Cannot dial" is not one state. connecting and busy are transient and
 *   render plain; only blocked -- no agent row, or no number assigned -- is
 *   grayed and explained, because only that one needs an administrator.
 * - The listener and the body flag go up and come down together, so there is
 *   never a moment where a cell looks clickable and nothing is listening. It is
 *   registered only while ready, which is also why a click mid-call cannot
 *   reach startCall and make the Twilio SDK throw InvalidStateError.
 */
export default function useClickToCall({
  state,
  blockedReason,
  onCall,
}: UseClickToCall) {
  useEffect(() => {
    const sweep = () => {
      Array.from(
        document.querySelectorAll<HTMLElement>(`[${CALL_ATTRIBUTE}]`),
      ).forEach((cell) => {
        const number = normalizeDialable(
          cell.getAttribute(CALL_ATTRIBUTE) || "",
        );

        if (number === null) {
          cell.removeAttribute(CALL_ATTRIBUTE);
          return;
        }

        // Written back normalised, so the click path never re-derives it and a
        // second pass over an already-swept table is a no-op.
        if (cell.getAttribute(CALL_ATTRIBUTE) !== number) {
          cell.setAttribute(CALL_ATTRIBUTE, number);
        }

        if (state === "blocked") cell.setAttribute("title", blockedReason);
        else cell.removeAttribute("title");
      });
    };

    sweep();

    // The paginated customer list renders new rows on every page change,
    // long after this effect ran. Watching for added nodes (not attribute
    // changes, which the sweep itself makes) keeps those rows on the same
    // rules as the first page.
    const observer = new MutationObserver(sweep);
    observer.observe(document.body, { childList: true, subtree: true });

    document.body.setAttribute(STATE_ATTRIBUTE, state);

    if (state !== "ready") {
      return () => {
        observer.disconnect();
        document.body.removeAttribute(STATE_ATTRIBUTE);
      };
    }

    const handleClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      const cell = target?.closest?.(`[${CALL_ATTRIBUTE}]`);
      const number = cell?.getAttribute(CALL_ATTRIBUTE);

      if (!number) return;

      event.preventDefault();
      onCall(number);
    };

    document.addEventListener("click", handleClick);

    return () => {
      observer.disconnect();
      document.removeEventListener("click", handleClick);
      document.body.removeAttribute(STATE_ATTRIBUTE);
    };
  }, [blockedReason, onCall, state]);
}
